import React, { FC, useEffect } from "react";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useTranslation } from "react-i18next";
import ImportFileModalView, {
  ImportFileModalViewProps,
  isAllowedExtension,
} from "../../shared/import-file-modal-view/import-file-modal-view";
import { convertGeometryToArcgis } from "../../services/api";
import { setConvertedFeatures } from "../../services/actions/map";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import {
  setConvertedGeometry,
  setImportModalError,
  setImportModalIsLoading,
  setImportModalSelectedCoordinateSystem,
  setImportModalSelectedFiles,
  setImportModalUploadedFiles,
} from "../../services/reducers/map";

interface ImportFileModalMapControlsProps {
  onClose: () => void;
}

const EXT_FILES = ["shp", "shx", "dbf", "csv", "dxf"];

const ImportFileModalMapControls: FC<ImportFileModalMapControlsProps> = ({
  onClose,
}) => {
  const {
    selectedFiles,
    selectedCoordinateSystem,
    error,
    isLoading,
    uploadedFiles,
  } = useAppSelector((state) => state.map.importModal);

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (error) {
      dispatch(setImportModalError(null));
    }
  }, [selectedFiles, selectedCoordinateSystem]);

  const processFiles = (files: File[]) => {
    const validFiles = files.filter((file) => {
      return isAllowedExtension(file, EXT_FILES);
    });
    const invalidFiles = files.filter(
      (file) => !isAllowedExtension(file, EXT_FILES),
    );

    if (invalidFiles.length > 0) {
      const names = invalidFiles.map((f) => f.name).join(", ");
      dispatch(
        setImportModalError(
          t("import-file-modal.unsupportedFormat", { names }),
        ),
      );
      return;
    }

    const existing = new Set([
      ...selectedFiles.map((f) => f.name),
      ...uploadedFiles.map((f) =>
        f.extension ? `${f.name}.${f.extension}` : f.name,
      ),
    ]);

    const unique = validFiles.filter((f) => !existing.has(f.name));
    const dupes = validFiles.filter((f) => existing.has(f.name));

    if (dupes.length > 0) {
      const names = dupes.map((d) => d.name).join(", ");
      dispatch(
        setImportModalError(
          t(
            dupes.length === 1
              ? "import-file-modal.fileAlreadyAddedOne"
              : "import-file-modal.fileAlreadyAddedMany",
            { names },
          ),
        ),
      );
      return;
    } else {
      dispatch(setImportModalError(null));
    }

    if (unique.length > 0) {
      dispatch(setImportModalSelectedFiles([...selectedFiles, ...unique]));
    }
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0 || selectedCoordinateSystem === null) return;

    const requiredExt = ["shp", "shx", "dbf"];

    const filesByBase: Record<string, Set<string>> = {};
    for (const file of selectedFiles) {
      const parts = file.name.split(".");
      const ext = parts.pop()!.toLowerCase();
      const base = parts.join(".");
      if (!filesByBase[base]) filesByBase[base] = new Set();
      filesByBase[base].add(ext);
    }

    const errors: string[] = [];
    for (const [base, exts] of Object.entries(filesByBase)) {
      const hasAnyRequired = requiredExt.some((ext) => exts.has(ext));
      if (!hasAnyRequired) continue;
      const missing = requiredExt.filter((e) => !exts.has(e));
      if (missing.length > 0) {
        const list = missing.map((e) => `.${e}`).join(", ");
        errors.push(t("import-file-modal.missingFiles", { base, list }));
      }
    }

    if (errors.length > 0) {
      dispatch(setImportModalError(errors.join("; ")));
      return;
    }

    try {
      dispatch(setImportModalIsLoading(true));
      const geometryByFile = await convertGeometryToArcgis(
        selectedCoordinateSystem,
        selectedFiles,
      );

      const geometryByFileMap: Record<string, any> = {};
      for (const fileObj of geometryByFile) {
        const layerName = fileObj.fileName.replace(/\.[^/.]+$/, "");
        const { fileName, ...rest } = fileObj;
        geometryByFileMap[fileObj.fileName] = { layerName, ...rest };
      }

      dispatch(setConvertedGeometry(geometryByFileMap));

      const filesToUpload = selectedFiles.map((file) => {
        const parts = file.name.split(".");
        const ext = parts.pop()!.toLowerCase();
        const name = parts.join(".");
        return { name, extension: ext };
      });

      dispatch(setImportModalUploadedFiles(filesToUpload));
      dispatch(setConvertedFeatures());

      dispatch(setImportModalSelectedFiles([]));
      dispatch(setImportModalSelectedCoordinateSystem(null));
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setImportModalIsLoading(false));
    }
  };

  const removeFile = (fileToRemove: File) => {
    const baseNameToRemove = fileToRemove.name.replace(/\.[^/.]+$/, "");
    dispatch(
      setImportModalSelectedFiles(
        selectedFiles.filter(
          (f) => f.name.replace(/\.[^/.]+$/, "") !== baseNameToRemove,
        ),
      ),
    );
  };

  const isSubmitDisabled =
    selectedFiles.length === 0 ||
    selectedCoordinateSystem === null ||
    isLoading;

  const importFileModalProps: ImportFileModalViewProps = {
    selectedFiles,
    selectedCoordinateSystem,
    error,
    handleSubmit,
    removeFile,
    acceptedFileTypes: ".shp,.shx,.dbf, .dxf, .csv",
    onCoordinateSystemChange: (value) =>
      dispatch(setImportModalSelectedCoordinateSystem(value)),
    onFilesSelected: processFiles,
    isSubmitDisabled,
    onClose,
  };

  return <ImportFileModalView {...importFileModalProps} />;
};

export default ImportFileModalMapControls;
